// Per-kanji etymology footnote — Outlier-style decomposition + glyph
// imagery + Middle Chinese on'yomi note. Surface-agnostic: same
// component drops into the n5 lifted card (variant='card') and the
// reader popover (variant='popover'). Variant only affects sizing.
//
// Lazy: collapsed by default; the server action fires on first open.
// A module-level promise cache de-duplicates concurrent expands across
// surfaces — clicking the same kanji-bearing lemma in the lifted card
// and the popover never makes two requests.

import { useCallback, useMemo, useState } from "react";
import {
  extractKanji,
  type EtymologyData,
  type ScriptForm,
} from "@bindery/ja-core";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@bindery/ui/components/ui/collapsible";
import { getEtymologyForLemma } from "../../lib/api";

type Variant = "card" | "popover";

type Resolved = { kanji: string; data: EtymologyData }[];

const promiseCache = new Map<string, Promise<Resolved>>();

interface Props {
  display: string;
  variant?: Variant;
}

export function EtymologySlot({ display, variant = "card" }: Props) {
  const kanji = extractKanji(display);
  const [open, setOpen] = useState(false);
  const [resolved, setResolved] = useState<Resolved | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const onOpenChange = useCallback(
    (next: boolean) => {
      setOpen(next);
      if (!next || resolved !== null || loading) return;
      setLoading(true);
      setError(null);
      let promise = promiseCache.get(display);
      if (!promise) {
        promise = getEtymologyForLemma(display);
        promiseCache.set(display, promise);
      }
      promise
        .then((data) => {
          setResolved(data);
          setLoading(false);
        })
        .catch((e: unknown) => {
          setError(e instanceof Error ? e.message : "etymology fetch failed");
          setLoading(false);
          // Clear cache so a retry can refetch.
          promiseCache.delete(display);
        });
    },
    [display, loading, resolved],
  );

  if (kanji.length === 0) return null;

  return (
    <div
      className="border-rule/70 border-y"
      style={{ background: "var(--paper-tint)" }}
    >
      <Collapsible open={open} onOpenChange={onOpenChange}>
        <CollapsibleTrigger asChild>
          <button
            type="button"
            className="n5-marginalia hover:text-ink flex w-full items-center justify-between px-4 py-1.5 text-left transition-colors"
          >
            <span>etymology</span>
            <span aria-hidden style={{ opacity: 0.7 }}>
              {open ? "▾" : "▸"}
            </span>
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="px-4 pt-1 pb-3">
            {loading && resolved === null ? (
              <p className="n5-marginalia py-2">consulting wiktionary…</p>
            ) : error ? (
              <p
                className="n5-marginalia py-2"
                style={{ color: "var(--ink-soft)" }}
              >
                etymology unavailable: {error}
              </p>
            ) : resolved ? (
              <KanjiSections sections={resolved} variant={variant} />
            ) : null}
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}

function KanjiSections({
  sections,
  variant,
}: {
  sections: Resolved;
  variant: Variant;
}) {
  const anyImagery = sections.some(
    (s) =>
      s.data.scriptForms && Object.values(s.data.scriptForms).some(Boolean),
  );
  const anyWiktionary = sections.some(
    (s) => s.data.source === "wiktionary" || s.data.source === "composite",
  );

  return (
    <div className="flex flex-col gap-3">
      {sections.map((s, i) => (
        <div
          key={s.kanji}
          className={i > 0 ? "border-rule/50 border-t pt-3" : undefined}
        >
          <KanjiSection section={s} variant={variant} />
        </div>
      ))}
      {(anyImagery || anyWiktionary) && (
        <p className="n5-marginalia" style={{ opacity: 0.75 }}>
          {anyImagery ? "imagery via Wikimedia Commons" : null}
          {anyImagery && anyWiktionary ? " · " : null}
          {anyWiktionary
            ? "prose adapted from Wiktionary (CC BY-SA 4.0)"
            : null}
        </p>
      )}
    </div>
  );
}

function KanjiSection({
  section,
  variant,
}: {
  section: { kanji: string; data: EtymologyData };
  variant: Variant;
}) {
  const { kanji, data } = section;
  const wikiUrl = `https://en.wiktionary.org/wiki/${encodeURIComponent(kanji)}#Japanese`;
  const imgPx = variant === "popover" ? 40 : 48;

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-baseline justify-between gap-3">
        <div className="flex min-w-0 items-baseline gap-2">
          <span
            style={{
              fontFamily:
                "var(--font-jp-learner), var(--font-jp-display), serif",
              fontSize: variant === "popover" ? "1.5rem" : "1.875rem",
              color: "var(--ink)",
              lineHeight: 1,
            }}
          >
            {kanji}
          </span>
          {data.gloss ? (
            <span
              style={{
                fontFamily: "var(--font-display), serif",
                fontStyle: "italic",
                color: "var(--ink-soft)",
                fontSize: "0.85rem",
              }}
            >
              {data.gloss}
            </span>
          ) : null}
        </div>
        <a
          href={wikiUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="n5-marginalia hover:text-ink underline-offset-2 hover:underline"
        >
          view on Wiktionary →
        </a>
      </div>

      <ScriptFormsRow forms={data.scriptForms} sizePx={imgPx} />

      <ProseLines data={data} variant={variant} />
    </div>
  );
}

function ScriptFormsRow({
  forms,
  sizePx,
}: {
  forms: EtymologyData["scriptForms"];
  sizePx: number;
}) {
  const [failedUrls, setFailedUrls] = useState<Set<string>>(new Set());

  const rows = useMemo(() => {
    if (!forms) return [];
    const out: { label: string; form: ScriptForm }[] = [];
    if (forms.oracleBone)
      out.push({ label: "oracle bone", form: forms.oracleBone });
    if (forms.chuSlip) out.push({ label: "chu slip", form: forms.chuSlip });
    if (forms.smallSeal)
      out.push({ label: "small seal", form: forms.smallSeal });
    return out.filter((r) => !failedUrls.has(r.form.url));
  }, [forms, failedUrls]);

  if (rows.length === 0) return null;

  return (
    <div className="flex items-end gap-3">
      {rows.map((r) => (
        <figure key={r.label} className="flex flex-col items-center gap-1">
          {/* A plain <img>: hotlinked Wikimedia asset, tiny dynamic per-kanji glyphs — no image pipeline would earn its keep here. */}
          <img
            src={r.form.url}
            alt={`${r.label} form`}
            loading="lazy"
            decoding="async"
            width={sizePx}
            height={sizePx}
            onError={() =>
              setFailedUrls((prev) => {
                if (prev.has(r.form.url)) return prev;
                const next = new Set(prev);
                next.add(r.form.url);
                return next;
              })
            }
            className="object-contain"
            style={{
              width: `${sizePx}px`,
              height: `${sizePx}px`,
              background: "var(--paper-tint)",
              border: "1px solid var(--rule)",
              padding: "2px",
            }}
          />
          <figcaption className="n5-marginalia" style={{ fontSize: "9px" }}>
            {r.label}
          </figcaption>
        </figure>
      ))}
    </div>
  );
}

function ProseLines({
  data,
  variant,
}: {
  data: EtymologyData;
  variant: Variant;
}) {
  const lines: { key: string; text: string; italic?: boolean }[] = [];

  if (data.decomposition)
    lines.push({ key: "decomposition", text: data.decomposition });

  if (data.semantic && data.phonetic) {
    lines.push({
      key: "components",
      text: `phonetic component: ${data.phonetic} · semantic component: ${data.semantic}`,
    });
  } else if (data.semantic) {
    lines.push({
      key: "components",
      text: `semantic component: ${data.semantic}`,
    });
  } else if (data.phonetic) {
    lines.push({
      key: "components",
      text: `phonetic component: ${data.phonetic}`,
    });
  }

  if (data.ancientForm)
    lines.push({ key: "ancientForm", text: data.ancientForm });

  if (data.onYomiOrigin)
    lines.push({ key: "onYomiOrigin", text: data.onYomiOrigin, italic: true });

  if (lines.length === 0) return null;

  return (
    <div
      className="flex flex-col gap-1"
      style={{
        fontFamily: "var(--font-display), serif",
        fontSize: variant === "popover" ? "0.78rem" : "0.85rem",
        color: "var(--ink)",
        lineHeight: 1.45,
      }}
    >
      {lines.map((l) => (
        <p key={l.key} style={l.italic ? { fontStyle: "italic" } : undefined}>
          {l.text}
        </p>
      ))}
    </div>
  );
}
